import React, { useState } from 'react';
import { authService } from '../../services/authService';
import { 
  Button, 
  TextField, 
  Container, 
  Typography, 
  Box, 
  Alert,
  AlertTitle
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

interface LoginFormProps {
  onToggleForm: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onToggleForm }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setUnverifiedEmail(false);

    try {
      const userCredential = await authService.login(email, password);
      const user = userCredential.user;
      
      // Check if email is verified
      if (!user.emailVerified) {
        setUnverifiedEmail(true);
        await authService.logout(); // Sign out unverified users
        return;
      }
      
      // Continue with normal login flow
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <img 
          src="/mylogo.png" 
          alt="Love is Green Logo" 
          style={{
            height: '150px',
            objectFit: 'contain',
            width: 'auto',
            marginBottom: '10px'
          }}
        />
        </Box>
      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        
        <Typography variant="h4" component="h2" align="center">
          Login
        </Typography>
        
        {error && <Alert severity="error">{error}</Alert>}
        
        {unverifiedEmail && (
          <Alert severity="warning">
            <AlertTitle>Email Not Verified</AlertTitle>
            Please verify your email address before logging in. Check your inbox for the verification link.
            <Button 
              onClick={async () => {
                try {
                  const userCredential = await authService.login(email, password);
                  await authService.resendVerificationEmail(userCredential.user);
                  await authService.logout();
                  setUnverifiedEmail(false);
                  setError('A new verification email has been sent.');
                } catch (err) {
                  setError('Failed to resend verification email.');
                }
              }}
              sx={{ mt: 1 }}
            >
              Resend Verification Email
            </Button>
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <Button 
            type="submit" 
            variant="contained" 
            fullWidth
            sx={{ mt: 1, color: 'white' }}
          >
            Login
          </Button>
        </Box>
        
        <Button 
          onClick={() => navigate('/register')}
          variant="text"
          fullWidth
        >
          Need an account? Register
        </Button>
      </Box>
    </Container>
  );
};

export default LoginForm;